import React from "react";
import { Link } from "react-router-dom";
import LandingChatBubble from "./LandingChatBubble";
import BrandLogo, { BrandIcon } from "./BrandLogo";

/**
 * Shared marketing layout — same header + footer used on Landing,
 * extracted so policy / industry pages stay consistent. Includes the
 * floating live chat bubble so visitors can try the product from any page.
 *
 * Note: Landing.jsx renders MarketingHeader / MarketingFooter directly (so
 * it can keep its own pricing-section anchors) — but it still mounts the
 * LandingChatBubble itself, hence why this layout also mounts it.
 */
export default function MarketingLayout({ children, footerCta = true }) {
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <MarketingHeader />
      <main className="flex-1">{children}</main>
      <MarketingFooter />
      <LandingChatBubble />
    </div>
  );
}

export function MarketingHeader() {
  return (
    <header className="border-b border-[#E2E8F0] bg-white/90 backdrop-blur sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-home-link">
          <BrandLogo size="md" />
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-[#4B5563]">
          <Link to="/" className="hover:text-[#030712]">Home</Link>
          <Link to="/use-cases" className="hover:text-[#030712]">Use cases</Link>
          <Link to="/blog" className="hover:text-[#030712]">Blog</Link>
          <Link to="/about" className="hover:text-[#030712]">About</Link>
          <Link to="/contact" className="hover:text-[#030712]">Contact</Link>
          <Link to="/#pricing" className="hover:text-[#030712]">Pricing</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium hover:underline" data-testid="nav-login-link">Sign in</Link>
          <Link to="/signup" className="btn-primary text-sm" data-testid="nav-signup-link">Start free</Link>
        </div>
      </div>
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer className="border-t border-[#E2E8F0] bg-white py-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-1">
          <Link to="/" className="inline-block">
            <BrandLogo size="md" />
          </Link>
          <p className="text-sm text-[#4B5563] mt-4 leading-relaxed">
            AI text, voice and calling agents for businesses worldwide.
          </p>
          <p className="text-xs font-mono text-[#4B5563] mt-4">
            A product of{" "}
            <a href="https://www.cloudlabweb.com" target="_blank" rel="noopener noreferrer" className="text-[#0044FF] hover:underline">
              Cloud Lab Technologies
            </a>{" "}
            · United Kingdom
          </p>
        </div>
        <div>
          <div className="font-display font-bold text-sm uppercase tracking-widest text-[#030712]">Product</div>
          <ul className="mt-4 space-y-2 text-sm text-[#4B5563]">
            <li><Link to="/#products" className="hover:text-[#0044FF]">Channels</Link></li>
            <li><Link to="/#integrations" className="hover:text-[#0044FF]">Integrations</Link></li>
            <li><Link to="/#capabilities" className="hover:text-[#0044FF]">Capabilities</Link></li>
            <li><Link to="/#pricing" className="hover:text-[#0044FF]">Pricing</Link></li>
            <li><Link to="/use-cases" className="hover:text-[#0044FF]">Use cases</Link></li>
            <li><Link to="/blog" className="hover:text-[#F97316]">Blog</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-display font-bold text-sm uppercase tracking-widest text-[#030712]">Industries</div>
          <ul className="mt-4 space-y-2 text-sm text-[#4B5563]">
            <li><Link to="/use-cases/healthcare" className="hover:text-[#0044FF]">Healthcare</Link></li>
            <li><Link to="/use-cases/ecommerce" className="hover:text-[#0044FF]">eCommerce</Link></li>
            <li><Link to="/use-cases/real-estate" className="hover:text-[#0044FF]">Real estate</Link></li>
            <li><Link to="/use-cases/education" className="hover:text-[#0044FF]">Education</Link></li>
            <li><Link to="/use-cases/restaurants" className="hover:text-[#0044FF]">Restaurants</Link></li>
            <li><Link to="/use-cases/law-firms" className="hover:text-[#0044FF]">Law firms</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-display font-bold text-sm uppercase tracking-widest text-[#030712]">Company</div>
          <ul className="mt-4 space-y-2 text-sm text-[#4B5563]">
            <li><Link to="/about" className="hover:text-[#0044FF]">About</Link></li>
            <li><Link to="/contact" className="hover:text-[#0044FF]">Contact</Link></li>
            <li><Link to="/terms" className="hover:text-[#0044FF]">Terms &amp; Conditions</Link></li>
            <li><Link to="/privacy" className="hover:text-[#0044FF]">Privacy Policy</Link></li>
            <li><Link to="/refund-policy" className="hover:text-[#0044FF]">Refund Policy</Link></li>
            <li><Link to="/ai-transparency" className="hover:text-[#0044FF]">AI Transparency</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-[#E2E8F0] flex flex-wrap items-center justify-between gap-3 text-xs font-mono text-[#4B5563]">
        <span>© 2026 HelloSir.ai · A Cloud Lab Technologies product · United Kingdom</span>
        <span>Worldwide · multi-tenant · enterprise-grade</span>
      </div>
    </footer>
  );
}
